/**
 * "Kalendarz" — five weekday tiles, four marks landing one after another, and a frame walking the
 * week. The phone's `CalendarWeekViz`, transcribed.
 *
 * It teaches one thing: a dated plan puts every training on a date, and a missed one does not move
 * the rest. That is why the tile it strikes through stays where it is while the frame carries on
 * past it — the illustration is an argument, not decoration, and it is shown at the moment the
 * trainer is choosing a scheduling mode they cannot change afterwards
 * (`self-paced-plan-requirements.md` R1).
 *
 * The five tiles are FIXED. Nothing here reads a plan, so the drawing cannot drift out of step
 * with one, and the same component serves the create dialog and the assign dialog's ambient state.
 *
 * `reducedMotion` and `running` are both required. A loop nobody asked for is the thing
 * `CLAUDE.md`'s motion policy exists to stop, and a loop left running behind a closed dialog is
 * work with no viewer.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CAL_MARK_STOPS,
  CAL_MISS_FADE,
  hopTrack,
} from './planSystemLoop';
import { useMeasuredLoop } from './useMeasuredLoop';

import styles from './planSystemViz.module.css';

/** The week the illustration draws, in the phone's own order. */
const WEEK = [
  { key: 'mon', state: 'done' },
  { key: 'tue', state: 'miss' },
  { key: 'wed', state: 'free' },
  { key: 'thu', state: 'due' },
  { key: 'fri', state: 'plain' },
] as const;

/** How many columns the frame walks — the first four tiles, which is where the phone stops it. */
const FRAME_STOPS = 4;

export interface PlanSystemVizProps {
  /** Whether the viewer asked for stillness. A parameter, never a media query — see the policy. */
  readonly reducedMotion: boolean;
  /** `false` parks the loop: the dialog is closed, or this system is not the one on screen. */
  readonly running: boolean;
  /** The figure under the drawing. Omitted where the illustration stands inside a live panel. */
  readonly foot?: boolean;
}

export function CalendarWeekViz({ reducedMotion, running, foot = true }: PlanSystemVizProps) {
  const { t } = useTranslation();
  const grid = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLSpanElement>(null);
  const done = useRef<HTMLSpanElement>(null);
  const miss = useRef<HTMLSpanElement>(null);
  const free = useRef<HTMLSpanElement>(null);
  const due = useRef<HTMLSpanElement>(null);
  const missTile = useRef<HTMLDivElement>(null);

  /**
   * The frame's travel is the rendered column PITCH, re-read whenever the box changes width.
   *
   * The mock computes it from `100cqw`; measuring gets the same number without the illustration
   * having to know how wide its own container is, and it is the only value here that is not a
   * constant.
   */
  useMeasuredLoop(
    grid,
    (box) => {
      const tiles = [...box.querySelectorAll<HTMLElement>(`.${String(styles.tile)}`)];
      const pitch = (tiles[1]?.offsetLeft ?? 0) - (tiles[0]?.offsetLeft ?? 0);
      const stops = Array.from({ length: FRAME_STOPS }, (_, column) => column * pitch);

      return [
        { elements: [frame.current], track: hopTrack(stops) },
        { elements: [done.current], track: CAL_MARK_STOPS.done },
        { elements: [miss.current], track: CAL_MARK_STOPS.miss },
        { elements: [free.current], track: CAL_MARK_STOPS.free },
        { elements: [due.current], track: CAL_MARK_STOPS.due },
        { elements: [missTile.current], track: CAL_MISS_FADE },
      ];
    },
    { reducedMotion, running },
  );

  const markRef = (state: (typeof WEEK)[number]['state']) =>
    state === 'done' ? done : state === 'miss' ? miss : state === 'free' ? free : due;

  return (
    <div className={styles.viz}>
      {/* One drawing, one sentence. The tiles below carry no accessible text of their own — a
        * screen reader reading five weekday abbreviations and four unlabelled marks learns
        * nothing the sentence does not say better. */}
      <div className={styles.calendar} ref={grid} role="img" aria-label={t('web:planSystemViz.calendar.alt')}>
        <span className={styles.frame} ref={frame} aria-hidden="true" />
        {WEEK.map(({ key, state }) => (
          <div
            key={key}
            className={`${String(styles.tile)} ${String(styles[state])}`}
            ref={state === 'miss' ? missTile : undefined}
            aria-hidden="true"
          >
            <span className={styles.day}>{t(`web:planSystemViz.calendar.day.${key}`)}</span>
            <span className={styles.mark} ref={state === 'plain' ? undefined : markRef(state)}>
              {state === 'done' && <TickGlyph />}
              {state === 'miss' && <CrossGlyph />}
            </span>
            <span className={styles.name}>{t(`web:planSystemViz.calendar.name.${key}`)}</span>
          </div>
        ))}
      </div>
      {foot && (
        <p className={styles.foot}>
          <b className={styles.figure}>{t('web:planSystemViz.calendar.figure')}</b>
          <span>{t('web:planSystemViz.calendar.footNote')}</span>
        </p>
      )}
    </div>
  );
}

function TickGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CrossGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
